'use client';

import type { ReactNode } from 'react';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Theme, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { ComponentErrorBoundary } from '@/components';
import { useT } from '@/hooks';
import { colors } from '@/styles/theme';

interface BreakingBannerProps {
  /** ISO timestamp from the API response's meta.generated_at */
  updatedAt?: string;
  risersCount: number;
  fallersCount: number;
}

function formatDateTime(iso: string | undefined, languageCode: string) {
  const d = iso ? new Date(iso) : new Date();
  const locale = languageCode === 'es' ? 'es-ES' : 'en-US';
  const date = d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  return { date, time };
}

export function BreakingBanner({ updatedAt, risersCount, fallersCount }: BreakingBannerProps) {
  const { t, language } = useT();
  const { date, time } = formatDateTime(updatedAt, language.code);

  return (
    <ComponentErrorBoundary componentName="Breaking Banner">
      <Box
        sx={{
          // Plain text on mobile (no card) — the boxed, gradient hero is a
          // sm+ treatment only, per design: the phone view sits directly on
          // the page background, matching the reference layout.
          background: (theme: Theme) => ({
            xs: 'none',
            sm:
              theme.palette.mode === 'dark'
                ? `linear-gradient(150deg, ${colors.text.primary.light} 0%, ${colors.bg.secondary.dark} 55%)`
                : `linear-gradient(150deg, ${alpha(theme.palette.primary.light, 0.35)} 0%, ${theme.palette.background.paper} 60%)`,
          }),
          // Longhand border props — NOT the `border` shorthand, which (with no
          // color of its own) resolves to `currentColor` and silently wins over
          // a separately-set `borderColor`, painting the border in the text
          // color instead of the intended tint.
          borderWidth: { xs: 0, sm: '1px' },
          borderColor: 'divider',
          borderRadius: { xs: 0, sm: '20px' },
          p: { xs: 0, sm: '32px', md: '40px 44px' },
          mb: '24px',
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: '24px', md: '40px' },
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-plex-mono), IBM Plex Mono, monospace',
              fontSize: '11.5px',
              letterSpacing: '0.14em',
              color: (theme: Theme) =>
                theme.palette.mode === 'dark' ? colors.neutral[400] : 'text.secondary',
              display: 'block',
              mb: '14px',
            }}
          >
            {date.toUpperCase()} · {t('breaking.updated')} {time}
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'var(--font-archivo), Archivo, sans-serif',
              fontWeight: 800,
              fontSize: { xs: '32px', sm: '44px', md: '52px' },
              lineHeight: 1.02,
              letterSpacing: '-0.035em',
              color: 'text.primary',
              m: 0,
              mb: '16px',
            }}
          >
            {t('breaking.title')}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '14.5px', sm: '16.5px' },
              color: (theme: Theme) =>
                theme.palette.mode === 'dark' ? '#b5aea4' : 'text.secondary',
              lineHeight: 1.5,
              maxWidth: 480,
            }}
          >
            {t('breaking.description')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: '20px', sm: '14px' }, flexShrink: 0 }}>
          <StatTile
            icon={<TrendingUpIcon sx={{ fontSize: { xs: 16, sm: 26 } }} />}
            value={`+${risersCount}`}
            label={t('breaking.risers')}
            main="#4ec38a"
            bg="#13251b"
            border="#1f4130"
            labelColor="#6f8f7c"
          />
          <StatTile
            icon={<TrendingDownIcon sx={{ fontSize: { xs: 16, sm: 26 } }} />}
            value={`−${fallersCount}`}
            label={t('breaking.fallers')}
            main="#e0664f"
            bg="#241615"
            border="#422321"
            labelColor="#8f6f6a"
          />
        </Box>
      </Box>
    </ComponentErrorBoundary>
  );
}

function StatTile({
  icon,
  value,
  label,
  main,
  bg,
  border,
  labelColor,
}: {
  icon: ReactNode;
  value: string;
  label: string;
  main: string;
  bg: string;
  border: string;
  labelColor: string;
}) {
  return (
    <Box
      sx={{
        // Tiny icon + inline numbers on mobile — the boxed tile is a sm+
        // treatment only, to match the plain (no-card) mobile hero.
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'center', sm: 'flex-start' },
        gap: { xs: '6px', sm: 0 },
        width: { xs: 'auto', sm: 132 },
        p: { xs: 0, sm: '20px' },
        borderRadius: { xs: 0, sm: '16px' },
        bgcolor: (theme: Theme) => ({
          xs: 'transparent',
          sm: theme.palette.mode === 'dark' ? bg : alpha(main, 0.12),
        }),
        // See the hero Box above for why this is longhand, not `border` shorthand.
        borderWidth: { xs: 0, sm: '1px' },
        borderStyle: 'solid',
        borderColor: (theme: Theme) => (theme.palette.mode === 'dark' ? border : alpha(main, 0.3)),
      }}
    >
      <Box sx={{ color: main, lineHeight: 1, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: { xs: 'baseline', sm: 'flex-start' },
          gap: { xs: '5px', sm: 0 },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'var(--font-plex-mono), IBM Plex Mono, monospace',
            fontSize: { xs: '13px', sm: '22px' },
            fontWeight: 600,
            color: main,
            mt: { xs: 0, sm: '12px' },
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'var(--font-plex-mono), IBM Plex Mono, monospace',
            fontSize: { xs: '9.5px', sm: '10.5px' },
            letterSpacing: '0.1em',
            color: (theme: Theme) =>
              theme.palette.mode === 'dark' ? labelColor : 'text.secondary',
            mt: { xs: 0, sm: '4px' },
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

export default BreakingBanner;
